import type { ColumnDef, SortingState } from '@tanstack/react-table';
import type { AuthMeResponse } from 'api/auth/Auth.schemas.ts';
import type { StatControllerListResponse } from 'api/stat/Stat.schema.ts';
import cx from 'clsx';
import dayjs from 'common/dayjs.ts';
import {
  formatShortAddress,
  fromQuerystring,
  GET,
  getActiveLeg,
  getAlertsToRead,
  getNextETA,
  getRuntimeTimezone,
  getStatusColor,
  isGranted,
  isPastLegMissingPodDatetime,
  POST,
  parseFormValues,
  stringToPosition,
  toQuerystring,
  unique,
  uniqueBy,
} from 'common/helpers.ts';
import { groupBy, isEqual, pullAt } from 'es-toolkit';
import type { DivIcon, LatLngExpression } from 'leaflet';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import type { Leg } from 'types/Leg.ts';
import type { Package } from 'types/Package.ts';
import { type SHIPMENT_STATUS, SHIPMENT_STATUSES, type Shipment } from 'types/Shipment.ts';
import { Button } from 'ui/component/Button.tsx';
import { IconBadge } from 'ui/component/IconBadge.tsx';
import { IClock, IFileSpreadsheet, IFilters, IInfo, IMap, ISearch, ITemperature, ITruck, IWarning } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tag } from 'ui/component/Tag.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { Input } from 'ui/control/Input.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { Toggle } from 'ui/control/Toggle.tsx';
import { usePageSizeInUrl } from 'ui/hook/usePageSizeInUrl.ts';
import { useRestoreScrollPositionOnBack } from 'ui/hook/useRestoreScrollPositionOnBack.ts';
import { useSessionPersistedFilters } from 'ui/hook/useSessionPersistedFilters.ts';
import { Datetime } from '#admin/component/Datetime.tsx';
import { DriverAssignmentDialog } from '#admin/component/DriverAssignmentDialog.tsx';
import type { Marker } from '#admin/component/Map.tsx';
import { MarkerPackage, MarkerSmartphone } from '#admin/component/markers.tsx';
import { ShipmentDialog } from '#admin/component/ShipmentDialog.tsx';
import { ShipmentListItemMenu } from '#admin/component/ShipmentListItemMenu.tsx';
import { getSWRKeyForPage, Table } from '#admin/component/table/index.ts';
import { useEnv } from '#admin/hook/useEnv.tsx';
import { useMe } from '#admin/hook/useMe.tsx';
import { useXLSXShipments } from '#admin/hook/useXLSXShipments.tsx';

const MapComponent = React.lazy(() => import('../component/Map.tsx'));

const getColumns = (
  me: AuthMeResponse | undefined,
  shipments: Shipment[] | undefined,
  showMap: boolean,
  buildOnClickDriverAssignHandler: (leg: Leg) => () => void,
  onClickDeleteShipment: (shipment: Shipment) => void,
  env: any,
  t: any,
): ColumnDef<Shipment>[] => {
  if (!me || !env || !shipments) return [];

  const adminLayout = isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID);

  const defaultColumns: ColumnDef<Shipment>[] = [
    {
      accessorKey: 'logistics_ex_id',
      header: t('labels.orderNumber'),
      size: 150,
      cell: (info) => {
        const value = info.getValue<string>();
        const shipment = info.row.original;
        if (!value) return null;

        const customerName = shipment.customer?.name;
        const withCustomerName = (content: React.ReactNode) => (
          <div className="flex flex-col gap-0.5">
            {content}
            {customerName && <span className="text-grey-dark text-xs">{customerName}</span>}
          </div>
        );

        if (shipment.state === 'draft' || !shipment.legs_count) return withCustomerName(value);

        const is_tracked = info.row.original.packages.some((pkg: Package) => pkg.tracker);
        const is_missing_pod = isPastLegMissingPodDatetime(info.row.original);
        const currentLeg = getActiveLeg(info.row.original);
        const nextETA = getNextETA(info.row.original);

        let is_delayed_tooltip = '';
        let is_delayed_color = '';
        if (currentLeg.delayed) {
          is_delayed_tooltip = t('tooltips.delayedShipment');
          is_delayed_color = 'text-red-dark';
        } else if (nextETA && nextETA < 30) {
          is_delayed_tooltip = t('tooltips.nextETA', { minutes: nextETA });
          is_delayed_color = 'text-yellow';
        }

        const render = (showTooltip: boolean) => (
          <Link className="anchor anchor-novisit flex flex-row items-center gap-1" to={`/shipment/${info.row.original.id}`} data-table-no-row-events>
            {showTooltip && !isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && (
              <Tooltip text={t('messages.driverAssignmentOptimization')}>
                <IInfo className="fill-grey-dark" />
              </Tooltip>
            )}
            {value}
            <div className="flex flex-row gap-0">
              {is_tracked && <ITemperature className="fill-grey-dark" title={t('tooltips.trackerAssociated')} />}
              {is_delayed_tooltip && <IClock className={is_delayed_color} title={is_delayed_tooltip} />}
              {is_missing_pod && <IWarning className="text-yellow" title={t('tooltips.missingPodDatetime')} />}
            </div>
          </Link>
        );

        if (!adminLayout || !currentLeg || !currentLeg.origin_postcode || !currentLeg.destination_postcode) return withCustomerName(render(false));

        const allCurrentLegs = shipments
          .filter((shipment) => shipment.legs_count > 0)
          .filter((shipment) => ['pending', 'transiting'].includes(shipment.status))
          .map((shipment) => getActiveLeg(shipment));

        if (allCurrentLegs.length <= 1) return withCustomerName(render(false));

        const getGroupingKey = (leg: Leg) => {
          if (leg.origin_postcode && leg.origin_expected_datetime && !leg.origin_actual_datetime) {
            return `origin_${leg.origin_postcode}_${leg.origin_expected_datetime}`;
          }
          // Suggestion: We could include more complex cases too, example:
          //    Leg 1 -> pickup at lab X @ 10:00 / delivery at hospital A @ 13:00
          //    Leg 2 -> pickup at hospital A @ 13:00 / delivery at hospital B @ 15:00
          // they could both be assigned to the same driver
          return 'no_group';
        };

        const groupedItems = groupBy(allCurrentLegs, getGroupingKey);

        const showTooltip = groupedItems[getGroupingKey(currentLeg)]?.length > 1;

        return withCustomerName(render(!!showTooltip));
      },
    },
    {
      accessorKey: 'collection_country',
      header: t('labels.from'),
      size: 170,
      cell: (info) => {
        const shipment = info.row.original;
        const { legs_count, collection_address_name, collection_city, collection_country, collection_postcode } = shipment;
        if (!legs_count) return null;

        const lines = formatShortAddress({ postcode: collection_postcode, city: collection_city, country: collection_country });
        return (
          <Tooltip text={collection_address_name || ''}>
            <span className="flex w-max flex-col items-center gap-1">
              {lines.map((line, i) => (
                <span key={i} className={cx(i === 0 && 'self-start')}>
                  {line}
                </span>
              ))}
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'collection_datetime',
      header: t('labels.collectionAt'),
      size: 160,
      cell: (info) => {
        const shipment = info.row.original;
        const { legs_count, collection_datetime, collection_tz } = shipment;
        if (!legs_count) return null;

        return (
          <Datetime timezone={collection_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'}>
            {collection_datetime}
          </Datetime>
        );
      },
    },
    {
      accessorKey: 'delivery_country',
      header: t('labels.to'),
      size: 170,
      cell: (info) => {
        const shipment = info.row.original;
        const { legs_count, delivery_address_name, delivery_city, delivery_country, delivery_postcode } = shipment;
        if (!legs_count) return null;

        const lines = formatShortAddress({ postcode: delivery_postcode, city: delivery_city, country: delivery_country });
        return (
          <Tooltip text={delivery_address_name || ''}>
            <span className="flex w-max flex-col items-center gap-1">
              {lines.map((line, i) => (
                <span key={i} className={cx(i === 0 && 'self-start')}>
                  {line}
                </span>
              ))}
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'delivery_datetime',
      header: t('labels.deliveryAt'),
      size: 160,
      cell: (info) => {
        const shipment = info.row.original;
        const { legs, delivery_datetime, delivery_tz } = shipment;
        if (!legs.length) return null;

        return (
          <Datetime timezone={delivery_tz || getRuntimeTimezone()} truncateRegion={true} layout={'y'}>
            {delivery_datetime}
          </Datetime>
        );
      },
    },
    {
      id: 'alerts',
      header: t('labels.alerts'),
      size: 100,
      cell: (info) => {
        // tofix: urgent! alerts on shipments is not correctly handled, we are pushing inside the package.alerts thus duplicating them for every package
        const alertsWithDupes = info.row.original.packages.flatMap((pkg: Package) => pkg.alerts);
        const alerts = alertsWithDupes.filter(uniqueBy('id'));
        const alertsToRead = getAlertsToRead(alerts);

        return (
          <Link to={`/alerts?search=${info.row.original.id}`} className="contents">
            <Tag className={cx('tag-alerts cursor-pointer', alertsToRead.length > 0 ? 'red' : 'grey')}>
              {t('labels.alertsUnreadOfTotal', { unread: alertsToRead.length, total: alerts.length })}
            </Tag>
          </Link>
        );
      },
    },
    {
      accessorKey: 'status',
      header: t('labels.status'),
      size: 140,
      cell: (info) => {
        const value = info.getValue<SHIPMENT_STATUS>();
        const renderTag = (status: SHIPMENT_STATUS) => <Tag className={cx('!p-2', getStatusColor(status))}>{t(`status.${status}`)}</Tag>;
        return value ? renderTag(info.row.original.status) : null;
      },
    },
    {
      id: 'actions',
      header: '',
      size: 40,
      meta: { className: '!p-0', hideFallback: true },
      cell: (info) => {
        const shipment = info.row.original;
        const currentLeg = shipment.legs_count ? getActiveLeg(shipment) : undefined;
        return (
          <ShipmentListItemMenu
            activeLeg={currentLeg}
            onClickDriverAssign={currentLeg ? buildOnClickDriverAssignHandler(currentLeg) : undefined}
            shipment={shipment}
            onClickDelete={() => onClickDeleteShipment(shipment)}
          />
        );
      },
    },
  ];

  // if map is open we remove the datetime cols are there's not enough space
  if (showMap) {
    const keysToRemove = ['collection_country', 'delivery_country', 'collection_datetime', 'delivery_datetime'];
    const indexes = keysToRemove.map((key) => defaultColumns.findIndex((col) => 'accessorKey' in col && col.accessorKey === key)).filter((index) => index !== -1);
    pullAt(defaultColumns, indexes);
  }

  // if role is agent, we have different/some columns to add relative to the leg this agent will be assigned to
  const agentColumns: ColumnDef<Shipment>[] = [
    {
      header: 'Driver',
      // the return of this accessorFn is then used on info.getValue() in the cell method
      accessorFn: (row) => {
        const activeLeg = getActiveLeg(row);
        return activeLeg?.driver;
      },
      size: 150,
      meta: { fallback: t('views.shipmentList.notAssigned'), fallbackClassName: 'text-red' },
      cell: (info) => {
        const value = info.getValue<Leg['driver']>();
        if (!value) return null;

        const { id, name: driverName } = value;
        return (
          <Link to={`/user/${id}`} className="anchor anchor-novisit">
            {driverName}
          </Link>
        );
      },
    },
  ];

  let columns = [...defaultColumns];
  // if user is supplier we need to show other columns
  if (!adminLayout) {
    columns.splice(1, 0, ...agentColumns);
    columns = columns.filter((column) => column.id !== 'alerts');
  }
  return columns;
};

type FormValues = {
  search: string;
  status$in: SHIPMENT_STATUS[];
  delayed: boolean | '';
  unassigned: boolean | '';
  unarchived_alerts: boolean | '';
  collection_datetime$from: string;
  collection_datetime$to: string;
  delivery_datetime$from: string;
  delivery_datetime$to: string;
  collection_postcode$in: string[];
  delivery_postcode$in: string[];
  actual_delivery_datetime$from: string;
  actual_delivery_datetime$to: string;
  with_supplier: string;
  with_driver: string;
  with_location: string;
  with_customers: string[];
  next_eta$from: string;
  next_eta$to: string;
};

const querystringParserOptions = { arrayKeys: ['status$in', 'with_customers', 'collection_postcode$in', 'delivery_postcode$in'], excludeKeys: ['size'] };
// stable identity so SelectMulti's options memo doesn't recompute on every parent render
const NO_OPTIONS: string[] = [];

export const ShipmentList = () => {
  const { t } = useTranslation();
  const formRef = React.useRef<HTMLFormElement>(null);
  const [searchParams, setSearchParams] = useSessionPersistedFilters('atlas:shipments-filters');
  const scrollRef = useRestoreScrollPositionOnBack('shipments-scroll');
  const [showFilters, setShowFilters] = React.useState<boolean>(false);
  const [sorters, setSorters] = React.useState<SortingState>([{ id: 'collection_datetime', desc: true }]);
  const [showMap, setShowMap] = React.useState<boolean>(window.localStorage.getItem('atlas:map') ? true : false);
  const [highlightedRow, setHighlightedRow] = React.useState<Shipment | null>(null);
  const [driverDialog, setDriverDialog] = React.useState<{ legs: Leg[] } | null>(null);
  const [deleteDialog, setDeleteDialog] = React.useState<Shipment | null>(null);
  const { me } = useMe();
  const env = useEnv();
  const defaultValues: FormValues = {
    search: '',
    status$in: [],
    delayed: '',
    unassigned: '',
    unarchived_alerts: '',
    collection_datetime$from: '',
    collection_datetime$to: '',
    delivery_datetime$from: '',
    delivery_datetime$to: '',
    collection_postcode$in: [],
    delivery_postcode$in: [],
    actual_delivery_datetime$from: '',
    actual_delivery_datetime$to: '',
    with_supplier: '',
    with_driver: '',
    with_location: '',
    with_customers: [],
    next_eta$from: '',
    next_eta$to: '',
  };
  const defaultValuesWithSearchValues = { ...defaultValues, ...fromQuerystring(searchParams, querystringParserOptions) };
  const [values, setValues] = React.useState<typeof defaultValues>(defaultValuesWithSearchValues);

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: defaultValuesWithSearchValues,
  });
  const { handleSubmit, formState, reset, setValue, getValues } = formContext;

  const { data: rowsCount, mutate: mutateCount } = useSWR(formState.isValid ? ['/shipment/count', values, parseFormValues] : null, POST);
  const swr = useSWRInfinite(getSWRKeyForPage('/shipment/list', 50, values, sorters, parseFormValues), POST, { revalidateFirstPage: false });
  const setSizeInUrl = usePageSizeInUrl(swr.setSize);
  const shipments: Shipment[] = React.useMemo(() => swr.data?.flat() || [], [swr]);
  const { exportToXLSX } = useXLSXShipments(shipments);
  const { data: stats } = useSWR<StatControllerListResponse>(['/stat/list', { include: ['ACTIVE', 'ETA_TODAY_TOMORROW'] }], POST);

  // TODO: @Bruno let's find another strategy here without shipment.legs
  const driversIds = React.useMemo(() => {
    return shipments
      .flatMap((shipment) => shipment.legs)
      .map((leg) => leg.driver_id)
      .filter(Boolean)
      .filter(unique);
  }, [shipments]);

  const { data: driversPositions } = useSWR(shipments.length > 0 ? ['/device/last-positions', { user_ids: driversIds }, parseFormValues] : null, POST);

  // autocomplete for filters
  const { data: locations } = useSWR(['/leg/utils/assigned-locations'], GET, { fallbackData: [] });
  const {
    data: { suppliers, drivers },
  } = useSWR(['/leg/utils/assigned-users'], GET, { fallbackData: { suppliers: [], drivers: [] } });
  const { data: customers } = useSWR<{ id: string; name: string }[]>(['/shipment/assigned-customers'], GET, { fallbackData: [] });

  const onClickDriverAssign = React.useCallback((leg: Leg) => () => setDriverDialog({ legs: [leg] }), []);

  const onClickDeleteShipment = React.useCallback((shipment: Shipment) => setDeleteDialog(shipment), []);
  const columns = React.useMemo(
    () => getColumns(me, shipments, showMap, onClickDriverAssign, onClickDeleteShipment, env, t),
    [me, shipments, showMap, onClickDriverAssign, onClickDeleteShipment, env, t],
  );

  const toggleFilters = () => setShowFilters(!showFilters);

  const resetFilters = () => {
    reset(defaultValues);
    window.setTimeout(triggerSubmit, 50);
  };

  const setFilter = (name: keyof FormValues, value: any) => () => {
    resetFilters();
    setValue(name, value, { shouldDirty: true, shouldTouch: true });
    triggerSubmit();
  };

  const setTodayTomorrowEtaFilter = () => {
    resetFilters();
    setValue('next_eta$from', dayjs().format('YYYY-MM-DD'), { shouldDirty: true, shouldTouch: true });
    setValue('next_eta$to', dayjs().add(1, 'day').format('YYYY-MM-DD'), { shouldDirty: true, shouldTouch: true });
    triggerSubmit();
  };

  // wait next tick or URL will not be updated
  const triggerSubmit = React.useCallback(() => window.setTimeout(() => formRef.current?.requestSubmit(), 50), []);

  const onSubmit = async (values: FormValues) => {
    setSearchParams(toQuerystring(values, querystringParserOptions), { replace: true });
    setValues(values);
    await swr.mutate();
  };

  const onRowMouseEnter = (row: Shipment) => setHighlightedRow(row);
  const onRowMouseLeave = () => setHighlightedRow(null);

  const toggleMap = () => {
    window.localStorage.setItem('atlas:map', !showMap ? '1' : '');
    setShowMap(!showMap);
  };

  // we need active filters: if filter is of type array then we count if having least 1 selection, otherwise we count if being not nullish
  const filtersCount = Object.values(values).filter((value) => (Array.isArray(value) ? value.length > 0 : value !== '' && value !== null)).length;

  const markers = React.useMemo(() => {
    if (!shipments.length) return [];

    const markers: Marker[] = [];

    for (const shipment of shipments) {
      if (me && env && !isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && shipment.status !== 'transiting') continue;

      let position: LatLngExpression;
      let markerIcon: DivIcon;

      const shipmentIsHighlighted = highlightedRow && shipment.id === highlightedRow.id ? true : false;
      const [pkg] = shipment.packages.filter((pkg: Package) => pkg.last_measured_at || pkg.tracker?.last_measured_at);
      const status = shipment.status;
      const activeLeg = getActiveLeg(shipment);

      if (!activeLeg || !['transiting', 'pending'].includes(status)) {
        continue;
      }

      if (!pkg) {
        if (activeLeg.status !== 'transiting') continue;
        if (!activeLeg.driver_id) continue;
        const driverPosition = (driversPositions || []).find((dp) => dp.user_id === activeLeg.driver_id);
        if (!driverPosition) continue;
        const driverPosition_1h_ago = dayjs(driverPosition.timestamp).isBefore(dayjs().subtract(1, 'hour'));
        const markerColor = driverPosition_1h_ago ? 'bg-red' : 'bg-green';

        position = driverPosition.position;
        markerIcon = MarkerSmartphone(cx(shipmentIsHighlighted && 'scale-150', markerColor));
      } else if (pkg.position) {
        position = stringToPosition(pkg.position);
        markerIcon = MarkerPackage(cx(shipmentIsHighlighted && 'scale-150'));
      } else if (pkg.tracker?.position) {
        position = stringToPosition(pkg.tracker.position);
        markerIcon = MarkerPackage(cx(shipmentIsHighlighted && 'scale-150'));
      } else {
        continue;
      }

      if (!position) continue;

      markers.push({
        // @ts-expect-error
        position: [...position].reverse() as LatLngExpression,
        icon: markerIcon,
        content: (
          <div className={'flex flex-col gap-2'}>
            Shipment <strong>{shipment.logistics_ex_id}</strong>
          </div>
        ),
      });
    }

    return markers;
  }, [shipments, highlightedRow, me, env, driversPositions]);

  // we can't rely only on formState.isDirty because it's true when defaultValues provided to useForm are changed, but we may have filters set by default provided in the URL
  // Custom dirty check: compare current values against truly empty state, not URL-derived defaults
  const currentValues = getValues();
  const clearFiltersEnabled = formState.isDirty || !isEqual(defaultValues, currentValues);

  // Apply button should be enabled if form is dirty OR if current values differ from what's been applied (values state)
  const applyButtonEnabled = formState.isDirty || !isEqual(currentValues, values);

  const onDriverAssignClose = () => setDriverDialog(null);
  const onDriverAssignSave = () => {
    setDriverDialog(null);
    void swr.mutate();
  };

  return (
    <main className="view-container">
      {driverDialog && (
        // TODO: @Bruno let's find another strategy here without shipment.legs
        <DriverAssignmentDialog key={driverDialog.legs[0].id} legs={driverDialog.legs} isOpen={!!driverDialog} onClose={onDriverAssignClose} onSave={onDriverAssignSave} />
      )}
      {deleteDialog && (
        <ShipmentDialog
          shipment={deleteDialog}
          isOpen={true}
          onSave={() => {
            setDeleteDialog(null);
            void swr.mutate();
            void mutateCount();
          }}
          onClose={() => setDeleteDialog(null)}
        />
      )}
      <section className="mb-4 flex h-16 flex-row gap-8">
        <button
          type="button"
          className="paper !p-3 flex flex-1 cursor-pointer select-none flex-row items-center justify-start gap-5"
          onClick={setFilter('status$in', ['pending', 'transiting'])}
        >
          <Tag className="blue !cursor-pointer !border-0 !p-2 self-center">
            <ITruck size={20} />
          </Tag>
          <div className="relative flex flex-row items-center gap-2">
            {stats === undefined && <Spinner size={24} />}
            {stats !== undefined && <h3 className="leading-[100%]">{stats.ACTIVE_SHIPMENTS}</h3>}
            <span>{t('views.shipmentList.activeShipments')}</span>
          </div>
        </button>

        {me && env && isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && (
          <button
            type="button"
            className="paper !p-3 flex flex-1 cursor-pointer select-none flex-row items-center justify-start gap-5"
            onClick={setFilter('unarchived_alerts', true)}
          >
            <Tag className="red !cursor-pointer !border-0 !p-2 self-center">
              <IWarning size={20} />
            </Tag>
            <div className="relative flex flex-row items-center gap-2">
              {stats === undefined && <Spinner size={24} />}
              {stats !== undefined && <h3 className="leading-[100%]">{stats.ACTIVE_ALERTED_SHIPMENTS}</h3>}
              <span>{t('views.shipmentList.shipmentsWithAlerts')}</span>
            </div>
          </button>
        )}

        <button type="button" className="paper !p-3 flex flex-1 cursor-pointer select-none flex-row items-center justify-start gap-5" onClick={setTodayTomorrowEtaFilter}>
          <Tag className="green !cursor-pointer !border-0 !p-2 self-center">
            <IClock size={20} />
          </Tag>
          <div className="relative flex flex-row items-center gap-2">
            {stats === undefined && <Spinner size={24} />}
            {stats !== undefined && <h3 className="leading-[100%]">{stats.ETA_TODAY_TOMORROW_SHIPMENTS}</h3>}
            <span>{t('views.shipmentList.shipmentsTodayTomorrow')}</span>
          </div>
        </button>
      </section>

      <section className="flex flex-row place-content-between items-baseline">
        <h4>{t('navigation.shipments')}</h4>

        <Button className="blue-outlined w-max" LeftIcon={IMap} onClick={toggleMap}>
          {t('actions.toggleMap')}
        </Button>
      </section>

      <div className={cx('mb-6 grid gap-8', showMap ? 'grid-cols-2' : 'grid-cols-1')}>
        <section className="mt-4 h-fit bg-white p-8 pb-4">
          <FormProvider {...formContext}>
            <form id="search-form" ref={formRef} className="mb-4 flex flex-col gap-8" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <div className="flex flex-row items-center gap-8">
                <Input
                  className="mr-auto w-[360px]"
                  name={'search'}
                  placeholder={t('placeholders.searchShipments')}
                  required={true}
                  autoFocus={true}
                  RightIcon={ISearch}
                  autoComplete={'off'}
                  debouncedAutoSubmitForm={'#search-form'}
                  debouncedAutoSubmitTimeout={500}
                />

                <Tooltip text={t('tooltips.exportToXlsx')}>
                  <Button className="!p-0 !w-auto mr-4" onClick={exportToXLSX}>
                    <IFileSpreadsheet size={20} className="blue" />
                  </Button>
                </Tooltip>

                <Toggle name={'unassigned'} label={t('filters.unassignedShipments')} autoSubmitForm={'#search-form'}>
                  {/*Shipments with at least one leg without a driver*/}
                  <Tooltip className="absolute top-1/2 -left-[18px] -translate-y-1/2" text={t('tooltips.atLeastOneUnassignedLeg')}>
                    <IInfo className="fill-gray-700" />
                  </Tooltip>
                </Toggle>

                <IconBadge icon={<IFilters className="size-[24px] fill-gray-400" />} badge={filtersCount || null} onClick={toggleFilters} />
              </div>

              {/* expanded filters section */}
              <div className={cx('filters-box', !showFilters && '!hidden')}>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-6">
                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.status')}
                    name={'status$in'}
                    options={SHIPMENT_STATUSES as unknown as string[]}
                    selectable={'many'}
                    disableSearch={true}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-1"
                    label={t('filters.delayed')}
                    name={'delayed'}
                    options={
                      new Map([
                        [true, t('filters.yes')],
                        [false, t('filters.no')],
                      ])
                    }
                    selectable={'one'}
                    disableSearch={true}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className={cx('col-span-1', me && env && !isGranted(me, 'ViewAdminLayout', env.SUPERADMIN_GROUP_ID) && 'hidden')}
                    label={t('filters.unarchivedAlerts')}
                    name={'unarchived_alerts'}
                    options={
                      new Map([
                        [true, t('filters.withAlerts')],
                        [false, t('filters.withoutAlerts')],
                      ])
                    }
                    selectable={'one'}
                    disableSearch={true}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  {me.group_role !== 'SUPPLIER' && (
                    <SelectMulti
                      className="col-span-1"
                      label={t('filters.supplier')}
                      name={'with_supplier'}
                      options={new Map(suppliers.map((supplier) => [supplier.id, supplier.name]))}
                      selectable={'one'}
                      closeOnChangedValue={true}
                      required={false}
                      hasClearAll={true}
                    />
                  )}

                  <SelectMulti
                    className="col-span-1"
                    label={t('filters.driver')}
                    name={'with_driver'}
                    options={new Map(drivers.map((supplier) => [supplier.id, supplier.name]))}
                    selectable={'one'}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-1"
                    label={t('filters.location')}
                    name={'with_location'}
                    options={locations}
                    selectable={'one'}
                    closeOnChangedValue={true}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.client')}
                    name={'with_customers'}
                    options={new Map((customers || []).map((c) => [c.id, c.name]))}
                    selectable={'many'}
                    closeOnChangedValue={false}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.collectionZip')}
                    name={'collection_postcode$in'}
                    options={NO_OPTIONS}
                    selectable={'many'}
                    dynamic={true}
                    placeholder={t('placeholders.typeZip')}
                    required={false}
                    hasClearAll={true}
                  />

                  <SelectMulti
                    className="col-span-2"
                    label={t('filters.deliveryZip')}
                    name={'delivery_postcode$in'}
                    options={NO_OPTIONS}
                    selectable={'many'}
                    dynamic={true}
                    placeholder={t('placeholders.typeZip')}
                    required={false}
                    hasClearAll={true}
                  />

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.collectionAt')}</span>
                    <div className="control-date-range">
                      <Input name={'collection_datetime$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'collection_datetime$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.deliveryAt')}</span>
                    <div className="control-date-range">
                      <Input name={'delivery_datetime$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'delivery_datetime$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.nextEta')}</span>
                    <div className="control-date-range">
                      <Input name={'next_eta$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'next_eta$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>

                  <div className="col-span-2 flex flex-col gap-[3.5px]">
                    <span className="text-grey-label">{t('filters.actualDeliveryAt')}</span>
                    <div className="control-date-range">
                      <Input name={'actual_delivery_datetime$from'} type={'date'} placeholder={t('labels.dateRange.from')} required={false} autoComplete={'off'} />
                      <Input name={'actual_delivery_datetime$to'} type={'date'} placeholder={t('labels.dateRange.to')} required={false} autoComplete={'off'} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-row gap-4">
                  <Button className="blue-outlined h-max min-w-max max-w-max p-1" type="button" onClick={toggleFilters}>
                    {t('actions.close')}
                  </Button>
                  <Button className="blue-outlined ml-auto min-w-max max-w-max" type="button" onClick={resetFilters} disabled={!clearFiltersEnabled}>
                    {t('actions.clearFilters')}
                  </Button>
                  <Button className="blue-outlined min-w-max max-w-max" disabled={showFilters && !applyButtonEnabled} type="submit">
                    {t('actions.apply')}
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>

          <Table
            scrollRef={scrollRef}
            className={cx('max-h-[calc(100vh-380px)]', !shipments.length && 'min-h-[300px]')}
            columns={columns}
            swr={{ ...swr, setSize: setSizeInUrl }}
            pagination={true}
            rowsCount={rowsCount}
            defaultSorting={sorters}
            onColumnSort={setSorters}
            estimateSize={50}
            overscan={50}
            onRowMouseEnter={onRowMouseEnter}
            onRowMouseLeave={onRowMouseLeave}
            wrapHeaders={showMap}
          />
        </section>

        {showMap && (
          <section className="relative mt-4 min-h-[calc(100vh-300px)]">
            {(swr.isValidating || markers === undefined) && <Spinner size={48} centered={true} />}
            {!swr.isValidating && markers !== undefined && (
              <React.Suspense fallback={<Spinner centered={true} />}>
                <MapComponent defaultZoom={4} markers={markers} clusterize={true} />
              </React.Suspense>
            )}
          </section>
        )}
      </div>
    </main>
  );
};
